import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { BilanEcf } from './useEcf';
import type { Avancement } from './useLecteur';
import type { ProgressionFormation, SyntheseEmargement } from './useEspaceApprenant';
import type { ChoixPeriode } from './useDashboardDirection';
import type { CommentaireSatisfaction, CritereSatisfaction, Nps } from './useQuestionnaires';

/** « Tous les sites » se traduit par l'absence de filtre côté API. */
function parametresSite(site: string): string {
  return site && site !== 'TOUS' ? `?site=${encodeURIComponent(site)}` : '';
}

// ---------------------------------------------------------------------------
// Fiches séquence (CDC §7.3) — délai, blocage et refus tenus par l'API : chaque
// ligne porte ce qui empêche chaque geste, l'écran ne recalcule rien.
// ---------------------------------------------------------------------------

export type EtatFiche = 'A_REDIGER' | 'BROUILLON' | 'RENVOYEE' | 'SOUMISE' | 'VALIDEE';
export type DemiJournee = 'MATIN' | 'APRES_MIDI';

export interface EmargementFiche {
  statut: 'PREVUE' | 'EN_COURS' | 'CLOTUREE';
  clos: boolean;
  attesteLe: string | null;
  attendus: number;
  presents: number;
  absents: number;
}

interface LigneCommune {
  seanceId: string | null;
  libelleSeance: string | null;
  date: string | null;
  demiJournee: DemiJournee | null;
  creneau: string | null;
  heuresCreneau: number | null;
  promotion: string | null;
  siteCode: string | null;
  modalite: 'PRESENTIEL' | 'ELEARNING' | null;
  blocs: string[];
  formateurId: string | null;
  formateur: string;
  emargement: EmargementFiche | null;
  echeance: string | null;
  joursRestants: number | null;
  horsDelai: boolean;
  prochaineEtape: string;
  etat: EtatFiche;
  etatLibelle: string;
  etatTon: string;
}

export interface LigneFiche extends LigneCommune {
  id: string;
  titre: string;
  objectifs: string | null;
  observations: string | null;
  heuresRealisees: number | null;
  soumiseLe: string | null;
  valideeLe: string | null;
  valideePar: string | null;
  renvoyeeLe: string | null;
  renvoyeePar: string | null;
  motifRenvoi: string | null;
  refusModification: string | null;
  refusRetrait: string | null;
  refusSoumission: string | null;
  refusValidation: string | null;
  refusRenvoi: string | null;
}

export interface InterventionADocumenter extends LigneCommune {
  refusCreation: string | null;
}

export interface BandeauFiches {
  ton: 'danger' | 'warning';
  titre: string;
  message: string;
  ficheId: string | null;
  seanceId: string | null;
}

export interface ReponseFiches {
  fiches: LigneFiche[];
  aDocumenter: InterventionADocumenter[];
  compteurs: {
    fichesDuMois: number;
    valideesDuMois: number;
    heuresDuMois: number;
    mois: string;
    aValider: number;
    aDocumenter: number;
    horsDelai: number;
    formateursBloques: number;
  };
  bandeau: BandeauFiches | null;
  delaiJours: number;
  estFormateur: boolean;
  peutValider: boolean;
}

export interface ActionEnAttente {
  seanceId: string;
  ficheId: string | null;
  libelle: string;
  etape: string;
  horsDelai: boolean;
  joursRestants: number;
}

export interface ReponseATraiter {
  nombre: number;
  rappel: { afficher: boolean; heure: string; bloque: boolean; actions: ActionEnAttente[] } | null;
}

export interface SaisieFiche {
  titre?: string;
  objectifs?: string | null;
  observations?: string | null;
  heuresRealisees?: number | null;
}

export function useFichesSequence(site: string) {
  const parametres = parametresSite(site);
  return useQuery({
    queryKey: ['fiches-sequence', parametres],
    queryFn: () => appelApi<ReponseFiches>(`/espace-formateur/fiches${parametres}`),
  });
}

/**
 * Ce qui attend le compte : pastille de navigation, et bandeau de rappel du
 * formateur. Relu toutes les cinq minutes : le bandeau paraît « vers 15 h 30 ».
 */
export function useFichesATraiter(actif: boolean) {
  return useQuery({
    queryKey: ['fiches-a-traiter'],
    queryFn: () => appelApi<ReponseATraiter>('/espace-formateur/fiches/a-traiter'),
    enabled: actif,
    refetchInterval: 5 * 60_000,
  });
}

function rafraichirFiches(client: QueryClient): Promise<unknown> {
  return Promise.all([
    client.invalidateQueries({ queryKey: ['fiches-sequence'] }),
    client.invalidateQueries({ queryKey: ['fiches-a-traiter'] }),
  ]);
}

export function useCreerFiche() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: SaisieFiche & { seanceId: string }) =>
      appelApi<{ fiche: LigneFiche }>('/espace-formateur/fiches', { methode: 'POST', corps }),
    onSuccess: () => rafraichirFiches(client),
  });
}

export function useModifierFiche() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, saisie }: { id: string; saisie: SaisieFiche }) =>
      appelApi<{ fiche: LigneFiche }>(`/espace-formateur/fiches/${encodeURIComponent(id)}`, {
        methode: 'PATCH',
        corps: saisie,
      }),
    onSuccess: () => rafraichirFiches(client),
  });
}

export function useRetirerFiche() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<void>(`/espace-formateur/fiches/${encodeURIComponent(id)}`, { methode: 'DELETE' }),
    onSuccess: () => rafraichirFiches(client),
  });
}

function geste(chemin: 'soumission' | 'validation') {
  return (id: string) =>
    appelApi<{ fiche: LigneFiche }>(`/espace-formateur/fiches/${encodeURIComponent(id)}/${chemin}`, {
      methode: 'POST',
    });
}

export function useSoumettreFiche() {
  const client = useQueryClient();
  return useMutation({ mutationFn: geste('soumission'), onSuccess: () => rafraichirFiches(client) });
}

export function useValiderFiche() {
  const client = useQueryClient();
  return useMutation({ mutationFn: geste('validation'), onSuccess: () => rafraichirFiches(client) });
}

export function useRenvoyerFiche() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) =>
      appelApi<{ fiche: LigneFiche }>(`/espace-formateur/fiches/${encodeURIComponent(id)}/renvoi`, {
        methode: 'POST',
        corps: { motif },
      }),
    onSuccess: () => rafraichirFiches(client),
  });
}

// ---------------------------------------------------------------------------
// Fin de formation (CDC §7.5) — mêmes règles que le moteur d'alertes
// ---------------------------------------------------------------------------

export interface LigneFinFormation {
  apprenantId: string;
  apprenant: string;
  siteCode: string;
  mode: 'CFA' | 'OF';
  formation: string | null;
  promotion: string;
  dateFin: string | null;
  provenanceFin: string | null;
  dateExamen: string | null;
  joursAvantFin: number | null;
  palier: number | null;
  alerteLibelle: string;
  alerteTon: string;
  ecf: BilanEcf | null;
  ecfMotif: string | null;
  certificat: 'DELIVRE' | 'A_PREPARER' | null;
}

export interface ReponseFinFormation {
  apprenants: LigneFinFormation[];
  compteurs: {
    promotions: number;
    suivis: number;
    /** Au premier palier (6 mois par défaut), puis au second (3 mois). */
    alertePremierPalier: number;
    alerteSecondPalier: number;
    examensProgrammes: number;
    ecfAttendues: number;
    ecfRealisees: number;
    ecfASaisir: number;
  };
  /** Paliers d'alerte en vigueur, en mois avant la fin : réglés par la direction (TF-435). */
  paliers: [number, number];
}

export function useFinFormation(site: string) {
  const parametres = parametresSite(site);
  return useQuery({
    queryKey: ['fin-formation', parametres],
    queryFn: () => appelApi<ReponseFinFormation>(`/espace-formateur/fin${parametres}`),
  });
}

// ---------------------------------------------------------------------------
// Tableau de bord (CDC §7, §7.1 ; TF-456) — apprenants dont le formateur est
// référent, avec leurs dates et leurs convocations, ses sessions du jour et
// leur accès direct, ses promotions et sa file de corrections. Dates, états,
// refus et ordre viennent de l'API.
// ---------------------------------------------------------------------------

export interface DateCleLue {
  /** Jour civil, `AAAA-MM-JJ`. */
  date: string;
  provenance: string | null;
}

export type EtatSuivi =
  | 'ENTREE_A_FIXER'
  | 'ENTREE_PROCHE'
  | 'A_VENIR'
  | 'EN_FORMATION'
  | 'FIN_PROCHE'
  | 'FIN_PASSEE';

export interface ConvocationRecue {
  documentId: string;
  type: 'CONVOCATION' | 'CONVOCATION_EXAMEN';
  libelle: string;
  envoyeeLe: string;
}

export interface ApprenantDuFormateur {
  apprenantId: string;
  apprenant: string;
  formation: string | null;
  siteCode: string;
  mode: 'CFA' | 'OF';
  debut: DateCleLue | null;
  fin: DateCleLue | null;
  examen: DateCleLue | null;
  etat: EtatSuivi;
  etatLibelle: string;
  etatTon: string;
  nouveau: boolean;
  referentDepuis: string | null;
  convocations: ConvocationRecue[];
}

/**
 * Où mène l'accès direct : la feuille d'une session, la création pré-remplie
 * de celle d'une promotion d'apprentissage, une séance hors session, la salle
 * à distance.
 */
export type AccesSessionDuJour =
  | { type: 'SESSION'; sessionId: string; demiJournee: DemiJournee }
  | {
      type: 'CREATION';
      formationCode: string;
      siteCode: string;
      demiJournee: DemiJournee;
      heureDebut: string;
      heureFin: string;
      libelle: string;
    }
  | { type: 'SEANCE'; seanceId: string }
  | { type: 'SALLE'; seanceId: string };

export interface SessionDuJour {
  cle: string;
  /** `null` : la feuille de la session n'est pas encore ouverte. */
  seanceId: string | null;
  libelle: string;
  promotion: string | null;
  siteCode: string;
  demiJournee: DemiJournee;
  creneau: string;
  lieu: string | null;
  modalite: 'PRESENTIEL' | 'ELEARNING';
  etatLibelle: string;
  etatTon: string;
  attendus: number;
  /** Rien n'est fait, la feuille est commencée, ou elle est close (la classe tenue). */
  avancement: 'A_OUVRIR' | 'EN_COURS' | 'TERMINEE';
  acces: AccesSessionDuJour | null;
  refusAcces: string | null;
}

export interface PromotionDuFormateur {
  cle: string;
  libelle: string;
  formation: string;
  siteCode: string;
  mode: 'CFA' | 'OF';
  periode: { debut: string; fin: string } | null;
  apprenants: number;
  suivis: number;
  aujourdhui: { libelle: string; auCentre: boolean } | null;
  /** Taux de complétion moyen de ses apprenants (TF-458) ; `null` : aucun n'a de taux. */
  tauxMoyen: number | null;
  /** Apprenants en retard : devoirs, absentéisme ou progression. */
  enRetard: number;
}

export interface TravailACorriger {
  cle: string;
  devoirId: string;
  titre: string;
  type: 'QUIZ' | 'PROJET' | 'RAPPORT' | 'AUTRE';
  typeLibelle: string;
  contexte: string;
  apprenantId: string;
  apprenant: string;
  promotion: string;
  siteCode: string;
  renduLe: string;
  enRetard: boolean;
  depot: number | null;
  noteAutomatique: { bonnes: number; questions: number; sur20: number } | null;
  /** Le formateur qui corrige ; `null` quand aucun n'est actif. */
  correcteur: string | null;
  /** Une correction est enregistrée en brouillon (TF-457). */
  brouillon: boolean;
}

export interface TableauDeBordFormateur {
  formateur: { statut: 'SALARIE' | 'INDEPENDANT'; statutLibelle: string } | null;
  apprenants: ApprenantDuFormateur[];
  sessionsDuJour: SessionDuJour[];
  prochaineSession: string | null;
  promotions: PromotionDuFormateur[];
  corrections: {
    compteurs: { devoirs: number; quiz: number; enRetard: number };
    /** Les premiers de la file, le plus ancien rendu d'abord. */
    lignes: TravailACorriger[];
  };
  compteurs: {
    apprenants: number;
    nouveaux: number;
    entreesProchaines: number;
    /** Préavis de l'intégration imminente, réglé par la direction (TF-435). */
    preavisIntegrationJours: number;
    demiJourneesAujourdhui: number;
    demiJourneesDuMois: number;
  };
}

export function useTableauDeBordFormateur() {
  return useQuery({
    queryKey: ['tableau-de-bord-formateur'],
    queryFn: () => appelApi<TableauDeBordFormateur>('/espace-formateur/tableau-de-bord'),
    // Feuilles ouvertes, travaux rendus : relu à chaque retour sur l'écran.
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
}

/** Adresse de l'accès direct : l'écran d'émargement ou la salle, la séance désignée. */
export function lienAccesSession(acces: AccesSessionDuJour): string {
  if (acces.type === 'SALLE') return `/form-elearning?seance=${encodeURIComponent(acces.seanceId)}`;
  if (acces.type === 'SEANCE') return `/emargement?seance=${encodeURIComponent(acces.seanceId)}`;
  if (acces.type === 'CREATION') {
    const parametres = new URLSearchParams({
      creer: acces.formationCode,
      site: acces.siteCode,
      demi: acces.demiJournee,
      debut: acces.heureDebut,
      fin: acces.heureFin,
      libelle: acces.libelle,
    });
    return `/emargement?${parametres.toString()}`;
  }
  return `/emargement?session=${encodeURIComponent(acces.sessionId)}&demi=${acces.demiJournee}`;
}

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

/** Convocation d'un apprenant : route gardée, réservée à son formateur référent. */
export function urlConvocationApprenant(apprenantId: string, documentId: string): string {
  return `${BASE}/espace-formateur/apprenants/${encodeURIComponent(apprenantId)}/convocations/${encodeURIComponent(documentId)}`;
}

// ---------------------------------------------------------------------------
// Heures prévisionnelles (TF-419) — mois ouverts, périmètre et cumul tenus par
// l'API : l'écran ne recalcule rien.
// ---------------------------------------------------------------------------

export type EtatMoisPrevision = 'OUVERT' | 'FIGE' | 'A_VENIR';

export interface HeuresPrevues {
  minutes: number;
  /** Valeur de la case, en heures décimales. */
  valeur: number;
  texte: string;
}

export interface LignePrevisionSession {
  sessionId: string;
  libelle: string;
  formation: string | null;
  siteCode: string;
  debut: string;
  fin: string;
  apprenants: number;
  heures: HeuresPrevues | null;
  horsPerimetre: boolean;
}

export interface LignePrevisionApprenant {
  apprenantId: string;
  apprenant: string;
  formation: string | null;
  siteCode: string;
  debut: string | null;
  fin: string | null;
  session: { id: string; libelle: string } | null;
  heures: HeuresPrevues | null;
  horsPerimetre: boolean;
}

export interface MoisPropose {
  cle: string;
  libelle: string;
  etat: EtatMoisPrevision;
}

export interface VuePrevision {
  mois: string;
  libelle: string;
  etat: EtatMoisPrevision;
  refus: string | null;
  moisProposes: MoisPropose[];
  sessions: LignePrevisionSession[];
  apprenants: LignePrevisionApprenant[];
  total: HeuresPrevues;
  heuresMax: number;
}

export interface VuePrevisionFormateur extends VuePrevision {
  aFiche: boolean;
}

export interface SaisiePrevision {
  sessions: { sessionId: string; heures: number }[];
  apprenants: { apprenantId: string; heures: number }[];
}

/** Sans mois, l'API ouvre le mois à venir. */
export function useMaPrevision(mois: string | null) {
  return useQuery({
    queryKey: ['heures-previsionnelles', mois ?? 'a-venir'],
    queryFn: () =>
      appelApi<VuePrevisionFormateur>(
        `/espace-formateur/heures-previsionnelles${mois ? `?mois=${encodeURIComponent(mois)}` : ''}`,
      ),
  });
}

export function useEnregistrerMaPrevision() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ mois, saisie }: { mois: string; saisie: SaisiePrevision }) =>
      appelApi<VuePrevisionFormateur>(`/espace-formateur/heures-previsionnelles/${encodeURIComponent(mois)}`, {
        methode: 'PUT',
        corps: saisie,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['heures-previsionnelles'] }),
  });
}

// ---------------------------------------------------------------------------
// Heures réalisées (TF-420) — calendrier, écart et repère tenus par l'API :
// l'écran ne recalcule rien.
// ---------------------------------------------------------------------------

export type EtatDeclaration = 'A_VENIR' | 'A_DECLARER' | 'EN_RETARD' | 'DECLAREE' | 'FIGEE' | 'HORS_SERVICE';

export interface EcartHeures {
  minutes: number;
  sens: 'EGAL' | 'PLUS' | 'MOINS';
  /** « +3,5 h », « −2 h », « 0 h ». */
  texte: string;
}

export interface RepereHeures {
  minutes: number;
  feuillesOuvertes: number;
  demiJournees: number;
  /** « 28 h » */
  heures: string;
  /** « 28 h émargées · 1 demi-journée non clôturée » */
  texte: string;
}

export interface ColonnesDeclaration {
  prevu: HeuresPrevues | null;
  realise: HeuresPrevues | null;
  ecart: EcartHeures | null;
  repere: RepereHeures;
  horsPerimetre: boolean;
}

export type LigneDeclarationSession = Omit<LignePrevisionSession, 'heures'> & ColonnesDeclaration;
export type LigneDeclarationApprenant = Omit<LignePrevisionApprenant, 'heures'> & ColonnesDeclaration;

export interface MoisDeclarationPropose {
  cle: string;
  libelle: string;
  etat: EtatDeclaration;
  etatLibelle: string;
}

export interface VueDeclaration {
  mois: string;
  libelle: string;
  etat: EtatDeclaration;
  etatLibelle: string;
  refus: string | null;
  avis: string;
  calendrier: { ouverture: string; finCorrection: string };
  declaration: { declareeLe: string; corrigeeLe: string | null } | null;
  moisProposes: MoisDeclarationPropose[];
  sessions: LigneDeclarationSession[];
  apprenants: LigneDeclarationApprenant[];
  total: { prevu: HeuresPrevues; realise: HeuresPrevues | null; ecart: EcartHeures | null; repere: RepereHeures };
  heuresMax: number;
}

export interface VueDeclarationFormateur extends VueDeclaration {
  aFiche: boolean;
}

/** Sans mois, l'API ouvre le plus ancien mois à déclarer. */
export function useMaDeclaration(mois: string | null) {
  return useQuery({
    queryKey: ['heures-realisees', mois ?? 'a-declarer'],
    queryFn: () =>
      appelApi<VueDeclarationFormateur>(
        `/espace-formateur/heures-realisees${mois ? `?mois=${encodeURIComponent(mois)}` : ''}`,
      ),
  });
}

export function useDeclarerHeures() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ mois, saisie }: { mois: string; saisie: SaisiePrevision }) =>
      appelApi<VueDeclarationFormateur>(`/espace-formateur/heures-realisees/${encodeURIComponent(mois)}`, {
        methode: 'PUT',
        corps: saisie,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['heures-realisees'] }),
  });
}

// ---------------------------------------------------------------------------
// Suivi des promotions (TF-458) : taux, retards et signaux viennent de l'API
// (`domain/suivi-promotion.ts`) ; l'écran ne recompte rien.
// ---------------------------------------------------------------------------

export type SignalRetard = 'DEVOIRS' | 'ABSENTEISME' | 'PROGRESSION';

export interface ReglesDeSuivi {
  /** Seuil d'absentéisme de l'organisme, en %. */
  seuilAbsenteisme: number;
  /** Points sous le taux moyen de la promotion à partir desquels la progression est en retard. */
  ecartProgression: number;
}

export interface AbsenteismeSuivi {
  /** Au dixième ; `null` sans demi-journée sur une feuille clôturée. */
  taux: number | null;
  demiJournees: number;
  absences: number;
  justifiees: number;
  auDelaDuSeuil: boolean;
}

export interface ApprenantSuivi {
  id: string;
  nom: string;
  prenom: string;
  siteCode: string;
  referent: string | null;
  suiviParVous: boolean;
  /** Ce qui ferme ses cours en ligne (pas de compte), ou `null`. */
  refus: string | null;
  avancement: Avancement;
  quiz: Avancement & { scoreMoyen: number | null };
  devoirs: { demandes: number; aRendre: number; enRetard: number };
  absenteisme: AbsenteismeSuivi;
  derniereActivite: string | null;
  /** Vide : il suit. */
  signaux: SignalRetard[];
}

export interface CoursDeLaPromotion {
  id: string;
  titre: string;
  tauxMoyen: number | null;
}

export interface PromotionSuivie {
  cle: string;
  libelle: string;
  formation: string;
  siteCode: string;
  mode: 'CFA' | 'OF';
  periode: { debut: string; fin: string } | null;
  suivis: number;
  tauxMoyen: number | null;
  /** Apprenants hors de la moyenne : rien à suivre en ligne, ou pas de compte. */
  sansTaux: number;
  repartition: { cle: string; libelle: string; apprenants: number }[];
  enRetard: number;
  blocs: { id: string; libelle: string; numero: number; tauxMoyen: number | null; cours: CoursDeLaPromotion[] }[];
  transversal: CoursDeLaPromotion[];
  apprenants: ApprenantSuivi[];
}

export interface SuiviDesPromotions {
  /** La direction suit toutes les promotions de ses antennes. */
  vue: 'FORMATEUR' | 'DIRECTION';
  refus: string | null;
  calculeLe: string;
  regles: ReglesDeSuivi;
  compteurs: { promotions: number; apprenants: number; tauxMoyen: number | null; enRetard: number };
  promotions: PromotionSuivie[];
}

export interface DevoirSuivi {
  id: string;
  type: 'QUIZ' | 'PROJET' | 'RAPPORT' | 'AUTRE';
  titre: string;
  contexte: string;
  echeance: string;
  statut: 'A_RENDRE' | 'RENDU' | 'CORRIGE';
  enRetard: boolean;
  renduLe: string | null;
  /** Publiée, à défaut note automatique d'un quiz fait (provisoire). */
  note: { valeur: number; publiee: boolean } | null;
}

export interface FicheProgressionApprenant {
  vue: 'FORMATEUR' | 'DIRECTION';
  regles: ReglesDeSuivi;
  apprenant: ApprenantSuivi & { mode: 'CFA' | 'OF'; dateEntree: string | null; dateFinPrevue: string | null };
  formation: { titre: string; blocs: { id: string; libelle: string }[] };
  promotion: { cle: string; libelle: string; tauxMoyen: number | null };
  progression: ProgressionFormation;
  devoirs: DevoirSuivi[];
  assiduite: SyntheseEmargement;
  /** Le dossier s'ouvre au lecteur : la direction, ou son formateur référent (TF-490). */
  dossierOuvrable: boolean;
}

export const LIBELLES_SIGNAL_RETARD: Record<SignalRetard, string> = {
  DEVOIRS: 'Devoirs en retard',
  ABSENTEISME: 'Absentéisme',
  PROGRESSION: 'Progression',
};

export function useSuiviPromotions() {
  return useQuery({
    queryKey: ['suivi-promotions'],
    queryFn: () => appelApi<SuiviDesPromotions>('/espace-formateur/promotions'),
    // Leçons terminées, devoirs rendus, feuilles closes : relu à chaque retour.
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
}

export function useFicheProgression(apprenantId: string) {
  return useQuery({
    queryKey: ['suivi-promotions', 'apprenant', apprenantId],
    queryFn: () =>
      appelApi<FicheProgressionApprenant>(`/espace-formateur/apprenants/${encodeURIComponent(apprenantId)}/progression`),
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Statistiques (TF-459) : heures, vues, retours et période viennent de l'API ;
// l'écran met en forme.
// ---------------------------------------------------------------------------

export interface PeriodeStatistiques {
  choix: ChoixPeriode;
  du: string;
  au: string;
  /** Dernier jour lu : `au`, ou aujourd'hui pour une période qui court encore. */
  arreteeAu: string;
  libelle: string;
}

export interface FormateurDesStatistiques {
  id: string;
  nom: string;
  sites: string[];
}

export interface PointVues {
  du: string;
  au: string;
  court: string;
  libelle: string;
  vues: number;
}

export interface ModuleVu {
  id: string;
  titre: string;
  cours: { id: string; titre: string };
  vues: number;
  apprenants: number;
  /** Vues rapportées à celles du premier module, en %. */
  part: number;
}

export interface Statistiques {
  heures: {
    validees: number;
    enAttente: number;
    fichesValidees: number;
    precedentes: number;
    ecart: number | null;
  };
  apprenants: { enFormation: number; referent: number; promotions: number };
  vues: {
    total: number;
    apprenants: number;
    coursPublies: number;
    pas: 'JOUR' | 'SEMAINE' | 'MOIS';
    serie: PointVues[];
    modules: ModuleVu[];
  };
  satisfaction: {
    reponses: number;
    moyenne: number | null;
    notes: number;
    nps: Nps;
    envoyes: number;
    tauxReponse: number | null;
    criteres: CritereSatisfaction[];
    commentaires: CommentaireSatisfaction[];
    commentairesEnTout: number;
    message: string;
  };
}

export interface StatistiquesFormateur {
  vue: 'FORMATEUR' | 'DIRECTION';
  refus: string | null;
  periode: PeriodeStatistiques;
  comparaison: { du: string; au: string; libelle: string };
  options: {
    periodes: { valeur: ChoixPeriode; libelle: string }[];
    /** Direction : les formateurs de ses antennes. */
    formateurs: FormateurDesStatistiques[] | null;
  };
  formateur: FormateurDesStatistiques | null;
  statistiques: Statistiques | null;
  calculeLe: string;
}

export interface FiltreStatistiques {
  periode: ChoixPeriode;
  du: string;
  au: string;
  /** Fiche formateur choisie par la direction ; vide : le premier, ou soi-même. */
  formateur: string;
}

/** Une période libre incomplète n'est pas demandée. */
export function useStatistiquesFormateur(filtre: FiltreStatistiques) {
  const parametres = new URLSearchParams({ periode: filtre.periode });
  if (filtre.periode === 'LIBRE') {
    parametres.set('du', filtre.du);
    parametres.set('au', filtre.au);
  }
  if (filtre.formateur) parametres.set('formateur', filtre.formateur);
  const chaine = parametres.toString();
  const libreIncomplete = filtre.periode === 'LIBRE' && (!filtre.du || !filtre.au || filtre.du > filtre.au);
  return useQuery({
    queryKey: ['statistiques-formateur', chaine],
    queryFn: () => appelApi<StatistiquesFormateur>(`/espace-formateur/statistiques?${chaine}`),
    enabled: !libreIncomplete,
    placeholderData: keepPreviousData,
    // Fiches validées, vues et réponses arrivent en continu : relu à chaque retour.
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
}
